import { assert, FileUploader, Key, lang } from 'zf-infra';
import OSS from 'ali-oss';
import { debug } from '../../logger';
import { CONFIG } from '../../config';
import { net } from '../net';
import { media } from '../../media';
import type { CompressParams } from '../../media/types';
import type { LocalStore } from '../../store';
import type { UploadOSSConfig, UploadParams } from 'zf-infra';
import type { Checkpoint, STSTokenResponse } from './types';

export class AliOSSUploader extends FileUploader {

  private readonly local?: LocalStore;
  // 存储每个文件的上传任务
  private readonly uploadTasks: Map<string, OSS>;

  constructor(local?: LocalStore) {
    super();
    this.local = local;
    this.uploadTasks = new Map();
  }

  async getClient(ossConfig?: UploadOSSConfig): Promise<OSS> {
    let baseToken = ossConfig?.token;
    if (baseToken == null) {
      baseToken = await this.fetchToken();
    }
    debug.infra('token', baseToken);

    return new OSS({
      accessKeyId: baseToken.AccessKeyId,
      accessKeySecret: baseToken.AccessKeySecret,
      stsToken: baseToken.SecurityToken,
      endpoint: ossConfig?.endpoint ?? CONFIG.OSS_ENDPOINT,
      bucket: ossConfig?.bucket ?? CONFIG.OSS_BUCKET,
      secure: true
    });
  }

  async upload(file: File | Blob, option?: UploadParams): Promise<string> {
    assert(this.local != null, 'local store cannot be empty');

    const { code } = this.local;
    assert(code != null, 'code cannot be empty');

    // 生成文件名
    const fileKey = new Key().generate(code, option?.prefix);
    debug.infra('file key', fileKey);

    // 开始值为0进度（将获取token的时间包含在内）
    option?.progress?.({ file, index: 1, percent: 0, totalBytesExpectedToSend: file.size });

    // 上传文件
    const client = await this.getClient(option?.ossConfig);

    // 保存任务
    this.uploadTasks.set(fileKey, client);

    const { name } = await client.multipartUpload(fileKey, file, {
      checkpoint: option?.checkpoint,
      progress: (percent: number, checkpoint?: Checkpoint) => {
        debug.infra(percent, checkpoint);
        option?.progress?.({
          file,
          index: 1,
          percent: lang.toFixed(percent),
          totalBytesExpectedToSend: checkpoint?.fileSize ?? file.size,
          checkpoint
        });
      }
    });

    // 上传完成后移除任务
    this.uploadTasks.delete(fileKey);
    const compress = option?.compress ?? this.compressRate(file.size);

    const type = media.getFileType(file);

    if (file.size > 512 * 1024 && type === 'image' && option?.skipCompress !== true) {
      const compressOption = this.formatCompressParams({
        resize: option?.resize,
        quality: compress
      });

      debug.infra('compress option', compressOption);

      // TODO: 补充不足的ts类型
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-expect-error
      await client.processObjectSave(name, name, compressOption);
    }

    return name;
  }

  async abort(fileKey: string, uploadId: string): Promise<void> {
    const client = this.uploadTasks.get(fileKey);
    if (client != null) {
      await client.abortMultipartUpload(fileKey, uploadId);
      this.uploadTasks.delete(fileKey);
    }
  }

  // TODO: 可以缓存token
  private async fetchToken(): Promise<STSTokenResponse> {
    return await net.get<STSTokenResponse>('/api/alioss/token', { type: 'sts' });
  }

  private compressRate(size: number): number {
    const defaultSize = 1024 * 1024;

    if (size <= defaultSize) {
      return 100;
    }

    return Math.floor(100 - ((size - defaultSize) / size * 100));
  }

  /**
   * 转换为处理图片的参数，如 'image/resize,l_100/quality,Q_80'
   * @param params
   * @returns
   */
  private formatCompressParams(params: CompressParams): string {
    let result = 'image/format,jpg/';

    if (params.resize != null) {
      result = `${result}/resize,l_${params.resize}`;
    }

    if (params.quality != null && params.quality < 100) {
      result = `${result}/quality,Q_${params.quality.toFixed()}`;
    }

    return result;
  }

}
